import { useMemo, useState } from "react";
import { getPublicFileUrl } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import ScrollReveal from "./ScrollReveal";
import { LANDING } from "@/constants/testIds";

export default function ResultsGallery({ gallery, services = [] }) {
  const { t } = usePublicLang();
  const [filter, setFilter] = useState("all");

  const filterOptions = useMemo(() => {
    const ids = new Set(gallery?.filter((g) => g.service_id).map((g) => g.service_id));
    return services.filter((s) => ids.has(s.id));
  }, [gallery, services]);

  if (!gallery || gallery.length === 0) return null;

  const visible = filter === "all" ? gallery : gallery.filter((g) => g.service_id === filter);

  return (
    <section id="resultados" className="py-20 md:py-28 px-5 bg-white" data-testid={LANDING.resultsSection}>
      <div className="max-w-6xl mx-auto">
        <ScrollReveal className="text-center mb-8">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-[#292522]">{t("landing.results.sectionTitle")}</h2>
        </ScrollReveal>

        {filterOptions.length > 0 && (
          <div className="flex items-center justify-center gap-2 overflow-x-auto pb-1 mb-8 md:mb-12">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === "all" ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#6B6058] border-[rgba(168,149,130,0.28)]"}`}
              data-testid={`${LANDING.galleryFilterPrefix}all`}
            >
              {t("landing.treatments.allCategories")}
            </button>
            {filterOptions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setFilter(s.id)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === s.id ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#6B6058] border-[rgba(168,149,130,0.28)]"}`}
                data-testid={`${LANDING.galleryFilterPrefix}${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {visible.map((g, idx) => (
            <ScrollReveal key={g.id} delay={(idx % 3) * 0.08} data-testid={`${LANDING.galleryItemPrefix}${g.id}`}>
              {g.media_type === "video" ? (
                <video
                  src={getPublicFileUrl(g.image_url)}
                  className="w-full aspect-square object-cover rounded-2xl"
                  controls
                  playsInline
                  data-testid={`${LANDING.galleryVideoPrefix}${g.id}`}
                />
              ) : g.image_url ? (
                <img src={getPublicFileUrl(g.image_url)} alt={g.caption || "resultado"} className="w-full aspect-square object-cover rounded-2xl" />
              ) : (
                <div className="grid grid-cols-2 gap-1.5 rounded-2xl overflow-hidden">
                  <div className="relative aspect-square">
                    <img src={getPublicFileUrl(g.before_url)} alt={t("landing.results.before")} className="w-full h-full object-cover" />
                    <span className="absolute bottom-2 left-2 text-[10px] font-semibold uppercase tracking-wide bg-black/50 text-white px-2 py-0.5 rounded-full">{t("landing.results.before")}</span>
                  </div>
                  <div className="relative aspect-square">
                    <img src={getPublicFileUrl(g.after_url)} alt={t("landing.results.after")} className="w-full h-full object-cover" />
                    <span className="absolute bottom-2 left-2 text-[10px] font-semibold uppercase tracking-wide bg-[var(--brand-primary)] text-white px-2 py-0.5 rounded-full">{t("landing.results.after")}</span>
                  </div>
                </div>
              )}
              {g.caption && <p className="text-sm text-[#6B6058] mt-3 text-center">{g.caption}</p>}
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
